// Single fetch wrapper with auth header + 401 → /auth/login redirect.
// Returns parsed JSON for 2xx; throws ApiError otherwise.

import { clearAuth, getToken } from './auth';

export class ApiError extends Error {
  status: number;
  body: string;
  constructor(status: number, body: string) {
    super(`${status}: ${body.slice(0, 200)}`);
    this.status = status;
    this.body = body;
  }
}

export interface ApiOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;          // auto JSON-serialized when not FormData
  query?: Record<string, string | number | undefined | null>;
  raw?: boolean;           // skip JSON-parse, return Response
}

function buildUrl(path: string, query?: ApiOptions['query']): string {
  if (!query) return path;
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null) continue;
    params.set(k, String(v));
  }
  const qs = params.toString();
  return qs ? `${path}?${qs}` : path;
}

export async function api<T = unknown>(
  path: string,
  opts: ApiOptions = {},
): Promise<T> {
  const { body, query, headers: hdrs, raw, ...rest } = opts;
  const token = getToken();
  const isFormData = body instanceof FormData;
  const headers: Record<string, string> = {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(isFormData ? {} : body !== undefined ? { 'Content-Type': 'application/json' } : {}),
    ...((hdrs as Record<string, string>) || {}),
  };
  const init: RequestInit = {
    ...rest,
    headers,
    body: body === undefined ? undefined : isFormData ? body : JSON.stringify(body),
  };
  const r = await fetch(buildUrl(path, query), init);

  if (r.status === 401) {
    clearAuth();
    if (!location.pathname.startsWith('/auth')) {
      window.location.replace('/auth/login?redirect=' +
        encodeURIComponent(location.pathname + location.search));
    }
    throw new ApiError(401, 'unauthenticated');
  }

  if (!r.ok && r.status !== 204) {
    const txt = await r.text().catch(() => '');
    // ★ 撞上四阶段闸门(409 stage_gate)→ 喊一声,让阶段条**抖一下**。
    //   没有这一声,用户只在聊天里看到一句话、界面毫无反应 ——
    //   他分不清是「没做」还是「不能做」。(桌面那边是 stage-denied 事件,同一个意思。)
    if (r.status === 409 && txt.includes('stage_gate')) {
      window.dispatchEvent(new CustomEvent('al:stage-denied', { detail: txt }));
    }
    throw new ApiError(r.status, txt);
  }

  if (raw) return r as unknown as T;
  if (r.status === 204) return undefined as T;
  return (await r.json()) as T;
}

// Asset URL with token query (for <img src=> / <audio src=> that can't send Bearer)
// `proxy: true` requests the lightweight 720p preview proxy when available —
// the backend serves the proxy if ready, else the original (and builds the
// proxy in the background). Use for scrubbing heavy footage fast.
export function assetUrl(
  projectId: string, relPath: string, opts?: { proxy?: boolean },
): string {
  const token = getToken();
  const q = new URLSearchParams({ rel_path: relPath });
  if (token) q.set('token', token);
  if (opts?.proxy) q.set('proxy', 'true');
  return `/api/v2/projects/${projectId}/assets/raw?${q.toString()}`;
}

// Per-shot render artifacts (storyboard still / clip) live under the project's
// cache/ dir, not assets/ — RenderState stores a project-root-relative path
// (e.g. 'cache/ps_s1_ab/shot.mp4'). Served by the dedicated artifact route so
// the asset endpoint's assets/-only containment doesn't reject it. ?token= so
// the <img>/<video> (no Bearer header) authenticates.
export function renderArtifactUrl(projectId: string, relPath: string): string {
  const token = getToken();
  const q = new URLSearchParams({ rel_path: relPath });
  if (token) q.set('token', token);
  return `/api/v2/projects/${projectId}/state/artifact?${q.toString()}`;
}

export function withAuthQuery(url: string): string {
  const token = getToken();
  if (!token) return url;
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}token=${encodeURIComponent(token)}`;
}
